import { FormEvent, useState, useEffect } from "react";
import { Auth } from "aws-amplify";
import { useNavigate } from "react-router-dom";
import { Button, Input, Label, Card, CardContent, CardHeader } from "@hotelise/ui";
import { Home, Mail, ArrowRight, Sun, Moon } from "lucide-react";

const ForgotPassword = (): JSX.Element => {
	const [email, setEmail] = useState("");
	const [isLoading, setIsLoading] = useState(false);
	const [errorMessage, setErrorMessage] = useState("");
	const [isDarkMode, setIsDarkMode] = useState(false);
	const navigate = useNavigate();

	useEffect(() => {
		const stored = localStorage.getItem("theme");
		const prefersDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
		const dark = stored === "dark" || (!stored && prefersDark);
		setIsDarkMode(dark);
		document.documentElement.classList.toggle("dark", dark);
	}, []);

	const toggleTheme = () => {
		const next = !isDarkMode;
		setIsDarkMode(next);
		document.documentElement.classList.toggle("dark", next);
		localStorage.setItem("theme", next ? "dark" : "light");
	};

	const getErrorMessage = (error: any): string => {
		const type = error?.code || error?.name || error?.__type;
		switch (type) {
			case "LimitExceededException":
				return "Too many attempts. Please try again later.";
			case "UserNotFoundException":
				return "If an account exists for this email, a verification code has been sent.";
			default:
				return error?.message || "Something went wrong. Please try again.";
		}
	};

	const handleSubmit = async (event: FormEvent) => {
		event.preventDefault();
		setErrorMessage("");

		const normalizedEmail = email.trim().toLowerCase();
		if (!normalizedEmail) return;

		try {
			setIsLoading(true);
			await Auth.forgotPassword(normalizedEmail);
			navigate("/reset-password", { state: { email: normalizedEmail } });
		} catch (error: any) {
			// For UserNotFoundException, still navigate to reset page (security best practice)
			if (error?.code === "UserNotFoundException" || error?.name === "UserNotFoundException") {
				navigate("/reset-password", { state: { email: normalizedEmail } });
				return;
			}
			setErrorMessage(getErrorMessage(error));
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<div className="min-h-screen w-full flex flex-col items-center justify-center bg-background relative p-4 sm:p-6">
			{/* Theme toggle */}
			<Button
				variant="ghost"
				size="icon"
				onClick={toggleTheme}
				className="absolute top-4 right-4 z-20"
				type="button"
				aria-label="Toggle theme"
				title="Toggle theme"
			>
				{isDarkMode ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
			</Button>

			{/* Background pattern */}
			<div
				className="fixed inset-0 opacity-[0.015] dark:opacity-[0.03] pointer-events-none"
				style={{
					backgroundImage: `radial-gradient(circle at 1px 1px, currentColor 1px, transparent 0)`,
					backgroundSize: "40px 40px",
				}}
			/>

			<div className="relative z-10 w-full max-w-md space-y-6">
				{/* Branding */}
				<div className="flex justify-center">
					<div className="inline-flex items-center gap-3">
						<div className="w-12 h-12 bg-primary rounded-xl flex items-center justify-center shadow-lg shadow-primary/25">
							<Home className="h-6 w-6 text-primary-foreground" />
						</div>
						<h1 className="text-2xl font-bold tracking-tight">Hotelise</h1>
					</div>
				</div>

				{/* Card */}
				<Card className="border shadow-xl shadow-black/5 dark:shadow-black/20">
					<CardHeader className="text-center pb-2 pt-6">
						<h2 className="text-lg font-semibold">Forgot your password?</h2>
						<p className="text-sm text-muted-foreground">
							Enter your email and we&apos;ll send you a reset code
						</p>
					</CardHeader>
					<CardContent className="pb-6 px-5 sm:px-6">
						<form className="space-y-4" onSubmit={handleSubmit}>
							<div className="space-y-1.5">
								<Label htmlFor="email" className="text-xs">Email</Label>
								<div className="relative">
									<Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
									<Input
										id="email"
										type="email"
										placeholder="owner@example.com"
										className="pl-10 h-10"
										value={email}
										onChange={(e) => { setErrorMessage(""); setEmail(e.target.value); }}
									/>
								</div>
							</div>

							{errorMessage && (
								<div className="p-3 rounded-lg border border-destructive/50 bg-destructive/10 text-sm text-destructive" role="alert">
									{errorMessage}
								</div>
							)}

							<Button type="submit" className="w-full h-10 group" disabled={isLoading}>
								{isLoading ? "Sending…" : "Send Code"}
								<ArrowRight className="h-4 w-4 ml-2 transition-transform group-hover:translate-x-1" />
							</Button>
						</form>
					</CardContent>
				</Card>

				{/* Back link */}
				<p className="text-center text-sm text-muted-foreground">
					<Button
						variant="link"
						className="px-1 h-auto text-sm"
						onClick={() => navigate("/")}
						type="button"
					>
						Back to Sign In
					</Button>
				</p>

				{/* Footer */}
				<p className="text-center text-[10px] text-muted-foreground/60 pt-2">
					© 2026 Hotelise. All rights reserved.
				</p>
			</div>
		</div>
	);
};

export default ForgotPassword;
